import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import { cn } from '~/lib/cn';
import type { VariantProps } from '~/lib/variants';
import { variants } from '~/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';

const tag = variants({
  base: [
    'rounded-xs',
    'text-strong',
    'duration-300',
    'select-none',
    'items-baseline',
    'inline-flex',
    'items-center',
  ],
  variants: {
    variant: {
      primary: [
        'bg-primary-default',
        'hover:bg-primary-hover',
        'focus:bg-primary-pressed',
      ],
      secondary: [
        'outline',
        'outline-1',
        'outline-emphasis-subtle',
        'bg-emphasis-default',
        'hover:bg-emphasis-strong',
        'hover:outline-emphasis-stronger',
        'focus:bg-emphasis-strong',
        'focus:outline-emphasis-strong',
        'focus:outline-1',
      ],
      static: [
        'bg-emphasis-default',
        'hover:bg-emphasis-strong',
        'focus:bg-emphasis-strong',
      ],
      staticStrong: ['bg-emphasis-subtle'],
      active: cn('bg-default-inverse text-black'),
      info: cn('bg-info-alert text-strong-on-light'),
    },
    size: {
      lg: ['px-sm', 'py-xs', 'text-body-sm', 'gap-sm', 'leading-tight'],
      sm: ['px-sm', 'py-xs', 'text-caption-md', 'gap-xs', 'leading-none'],
    },
    disabled: {
      true: [
        'cursor-not-allowed',
        'pointer-events-none',
        'bg-emphasis-default',
      ],
    },
    interactive: {
      true: cn('cursor-pointer'),
    },
  },
  compoundVariants: [
    {
      disabled: true,
      variant: 'primary',
      size: ['lg', 'sm'],
      class: ['text-subtle'],
    },
    {
      disabled: true,
      variant: 'secondary',
      size: ['lg', 'sm'],
      class: ['text-disabled'],
    },
    {
      disabled: true,
      variant: 'active',
      class: ['text-subtle', 'bg-emphasis-subtle'],
    },
  ],
});

type TagVariants = VariantProps<typeof tag>;

type TagProps = TagVariants &
  ComponentProps<ComponentPropsWithoutRef<'span'>> & {
    asChild?: boolean;
  };

export const Tag = forwardRef<HTMLElement, TagProps>(
  (
    {
      variant = 'primary',
      size = 'lg',
      disabled,
      interactive,
      children,
      asChild = false,
      ...props
    }: TagProps,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const Component = asChild ? Slot : 'span';
    const className = getClassNameOverride(props, 'Tag');

    return (
      <Component
        ref={ref}
        {...props}
        className={cn(tag({ size, variant, disabled, interactive }), className)}
      >
        {children}
      </Component>
    );
  },
);

Tag.displayName = 'Tag';
